/**
 * Vault addresses and configuration for supported blockchain networks.
 *
 * @remarks
 * This module provides vault address configurations for different blockchain networks,
 * particularly focused on staking operations. It includes predefined vault addresses
 *
 * @module Vault Addresses
 * @category Internal
 * @packageDocumentation
 */
import { detectNamespace, normalizeChainID } from "./caip2";
import { ChainInvalidChainIdError, ChainInvalidNamespaceError } from "./error";
/**
 * Vault addresses and configuration for supported blockchain networks.
 *
 * @remarks
 * This module provides vault address configurations for different blockchain networks,
 * particularly focused on staking operations. It includes predefined vault addresses
 * and utility functions for accessing vault information across supported chains.
 *
 * The vault addresses are used for staking operations, allowing users to interact
 * with staking contracts and manage their staked assets across different networks.
 *
 * @example
 * ```typescript
 * import { ETHEREUM_HOODI_BD_PUBLIC_VAULT, VAULT_ADDRESSES_MAP } from "@blockdaemon/bd-sdk";
 *
 * // Get Hoodi public vault address
 * console.log("Hoodi public vault:", ETHEREUM_HOODI_BD_PUBLIC_VAULT);
 *
 * // Get vault for specific chain
 * const hoodiVault = VAULT_ADDRESSES_MAP["560048"];
 * console.log("Hoodi vault:", hoodiVault);
 * ```
 */
/**
 * Numeric chain ID for Hoodi testnet network.
 * @remarks Used for chain-specific operations and configurations.
 */
export const HOODI_CHAIN_ID = "560048";
/**
 * Chain ID for Hoodi testnet network.
 * @remarks CAIP-2 formatted chain identifier for the Hoodi testnet network.
 * @public
 */
export const hoodiTestnetChainID = `eip155:${HOODI_CHAIN_ID}`;
export const ethereumMainnetChainID = "eip155:1";
/**
 * Solana genesis hashes for CAIP-2 chain IDs.
 * @see https://github.com/ChainAgnostic/namespaces/blob/main/solana/caip2.md
 */
export const SOLANA_MAINNET_GENESIS = "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp";
export const SOLANA_TESTNET_GENESIS = "4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z";
/**
 * Solana chain IDs in canonical CAIP-2 format using genesis hashes.
 */
export const solanaMainnetChainID = `solana:${SOLANA_MAINNET_GENESIS}`;
export const solanaTestnetChainID = `solana:${SOLANA_TESTNET_GENESIS}`;
const SOLANA_ALIAS_TO_GENESIS_HASH = {
    mainnet: SOLANA_MAINNET_GENESIS,
    testnet: SOLANA_TESTNET_GENESIS,
};
/**
 * Normalizes Solana chain ID to canonical genesis hash format for vault lookup.
 * Converts aliases (mainnet/testnet) to their genesis hash equivalents.
 */
function normalizeSolanaVaultChainId(chainId) {
    if (!chainId.startsWith("solana:"))
        return chainId;
    const reference = chainId.split(":")[1];
    const genesisHash = reference ? SOLANA_ALIAS_TO_GENESIS_HASH[reference] : undefined;
    return genesisHash ? `solana:${genesisHash}` : chainId;
}
/**
 * Hoodi testnet public Blockdaemon vault address.
 * @remarks Public StakeWise vault operated by Blockdaemon on Hoodi testnet.
 * @see {@link https://app.stakewise.io/vault/hoodi/0xd704bbe7f20a6ab2cfca03a404380b340ad6f1cb} Vault on StakeWise
 */
export const ETHEREUM_HOODI_BD_PUBLIC_VAULT = "0xD704bBe7f20A6aB2cfCA03a404380B340ad6F1cb";
/**
 * Ethereum mainnet Blockdaemon vault address.
 * @remarks Public StakeWise vault operated by Blockdaemon on Ethereum mainnet.
 * @see {@link https://app.stakewise.io/vault/mainnet/0x5799be451ee9fddd171c23bf5be86eccfe1f062f} Vault on StakeWise
 */
export const ETHEREUM_BD_MAINNET_VAULT = "0x5799BE451eE9fddD171c23bf5BE86eCcFE1f062F";
/**
 * Solana mainnet Blockdaemon validator vote account.
 * @remarks Official Blockdaemon validator on Solana mainnet.
 *
 * **Important:** This is the Vote Account address, not the Validator Identity.
 * - Validator Identity: `7cVfgArCheMR6Cs4t6vz5rfnqd56vZq4ndaBrY5xkxXy`
 * - Vote Account: `FQwewNXahV7MiZcLpY6p1xhUs2acVGQ3U5Xxc7FzV571` (used for delegation)
 *
 * @see {@link https://www.validators.app/validators/7cVfgArCheMR6Cs4t6vz5rfnqd56vZq4ndaBrY5xkxXy?locale=en&network=mainnet} Validator on Validators.app
 * @see {@link https://explorer.solana.com/address/FQwewNXahV7MiZcLpY6p1xhUs2acVGQ3U5Xxc7FzV571} Vote Account on Solana Explorer
 */
export const SOLANA_BD_MAINNET_VAULT = "FQwewNXahV7MiZcLpY6p1xhUs2acVGQ3U5Xxc7FzV571";
/**
 * Solana testnet validator vote account for testing.
 * @remarks Third-party testnet validator. Blockdaemon operates validators on mainnet only.
 * Using an active community validator for development and testing.
 *
 * **Important:** This is the Vote Account address, not the Validator Identity.
 * - Validator Identity: `J7v9ndmcoBuo9to2MnHegLnBkC9x3SAVbQBJo5MMJrN1`
 * - Vote Account: `HiFjzpR7e5Kv2tdU9jtE4FbH1X8Z9Syia3Uadadx18b5` (used for delegation)
 *
 * @see {@link https://www.validators.app/validators/J7v9ndmcoBuo9to2MnHegLnBkC9x3SAVbQBJo5MMJrN1?network=testnet} Validator on Validators.app
 * @see {@link https://www.validators.app/validators/J7v9ndmcoBuo9to2MnHegLnBkC9x3SAVbQBJo5MMJrN1/vote_accounts/HiFjzpR7e5Kv2tdU9jtE4FbH1X8Z9Syia3Uadadx18b5?network=testnet} Vote Account Details
 * @see {@link https://explorer.solana.com/address/HiFjzpR7e5Kv2tdU9jtE4FbH1X8Z9Syia3Uadadx18b5?cluster=testnet} Vote Account on Solana Explorer
 */
export const SOLANA_BD_TESTNET_VALIDATOR = "HiFjzpR7e5Kv2tdU9jtE4FbH1X8Z9Syia3Uadadx18b5";
/**
 * Mapping of chain IDs to vault addresses.
 * @remarks
 * Simple mapping between blockchain chain IDs and their corresponding vault addresses.
 * Currently supports Hoodi testnet with Blockdaemon's public vault.
 *
 * @example
 * ```typescript
 * // Get vault address for Hoodi testnet
 * const hoodiVault = VAULT_ADDRESSES_MAP["560048"];
 * console.log("Hoodi vault address:", hoodiVault);
 *
 * // Check if chain has a vault
 * const hasVault = "560048" in VAULT_ADDRESSES_MAP;
 * console.log("Has Hoodi vault:", hasVault);
 * ```
 */
export const VAULT_ADDRESSES_MAP = {
    [hoodiTestnetChainID]: ETHEREUM_HOODI_BD_PUBLIC_VAULT, // Hoodi testnet - Blockdaemon public vault
    [ethereumMainnetChainID]: ETHEREUM_BD_MAINNET_VAULT, // Ethereum mainnet - Blockdaemon public vault
    [solanaMainnetChainID]: SOLANA_BD_MAINNET_VAULT, // Solana mainnet - Blockdaemon validator
    [solanaTestnetChainID]: SOLANA_BD_TESTNET_VALIDATOR, // Solana testnet - third-party validator (no BD presence)
};
/**
 * Gets the vault address for a specific chain ID.
 *
 * @param chainId - The chain ID to get the vault address for
 * @returns The vault address for the specified chain, or undefined if not supported
 *
 * @example
 * ```typescript
 * import { getVaultAddress } from "@blockdaemon/bd-sdk";
 *
 * // Get vault for Hoodi testnet
 * const hoodiVault = getVaultAddress("560048");
 * console.log("Hoodi vault:", hoodiVault);
 *
 * // Handle unsupported chain
 * const unknownVault = getVaultAddress("999999");
 * if (!unknownVault) {
 *   console.log("Chain not supported");
 * }
 * ```
 */
export function getVaultAddress(chainId) {
    let namespace;
    try {
        namespace = detectNamespace(chainId);
    }
    catch {
        throw new ChainInvalidNamespaceError(chainId);
    }
    let normalizedChainId;
    try {
        normalizedChainId = normalizeChainID(chainId, namespace);
    }
    catch (e) {
        const reason = e instanceof Error ? e.message : undefined;
        throw new ChainInvalidChainIdError(chainId, namespace, reason);
    }
    const vaultChainId = namespace === "solana" ? normalizeSolanaVaultChainId(normalizedChainId) : normalizedChainId;
    return VAULT_ADDRESSES_MAP[vaultChainId];
}
/**
 * Checks if a chain ID has a configured vault address.
 *
 * @param chainId - The chain ID to check
 * @returns True if the chain has a configured vault, false otherwise
 *
 * @example
 * ```typescript
 * import { hasVaultForChain } from "@blockdaemon/bd-sdk";
 *
 * // Check if Hoodi is supported
 * if (hasVaultForChain("560048")) {
 *   console.log("Hoodi testnet is supported");
 * }
 *
 * // Check unsupported chain
 * if (!hasVaultForChain("999999")) {
 *   console.log("Chain not supported");
 * }
 * ```
 */
export function hasVaultForChain(chainId) {
    let namespace;
    try {
        namespace = detectNamespace(chainId);
    }
    catch {
        throw new ChainInvalidNamespaceError(chainId);
    }
    let normalizedChainId;
    try {
        normalizedChainId = normalizeChainID(chainId, namespace);
    }
    catch (e) {
        const reason = e instanceof Error ? e.message : undefined;
        throw new ChainInvalidChainIdError(chainId, namespace, reason);
    }
    const vaultChainId = namespace === "solana" ? normalizeSolanaVaultChainId(normalizedChainId) : normalizedChainId;
    return vaultChainId in VAULT_ADDRESSES_MAP;
}
/**
 * Gets all supported chain IDs.
 *
 * @returns Array of supported chain IDs
 *
 * @example
 * ```typescript
 * import { getSupportedChains } from "@blockdaemon/bd-sdk";
 *
 * const supportedChains = getSupportedChains();
 * console.log("Supported chains:", supportedChains);
 * // Output: ["560048"]
 * ```
 */
// Note: `getSupportedChains` removed in favor of `ChainService.getAllChains()`
/**
 * Normalizes a vault address to lowercase hex with `0x` prefix.
 *
 * Known Blockdaemon vault addresses are returned in their canonical checksummed form.
 * All other addresses are lowercased (no EIP-55 checksum is applied).
 *
 * @param vaultAddress - The vault address to normalize
 * @returns The normalized vault address
 *
 * @example
 * ```typescript
 * import { normalizeEthVaultAddress } from "@blockdaemon/bd-sdk";
 *
 * // Known BD vault — returns canonical checksummed form
 * normalizeEthVaultAddress("0x5799be451ee9fddd171c23bf5be86eccfe1f062f");
 * // "0x5799BE451eE9fddD171c23bf5BE86eCcFE1f062F"
 *
 * // Unknown address — lowercased
 * normalizeEthVaultAddress("0xABCDEF1234567890ABCDEF1234567890ABCDEF12");
 * // "0xabcdef1234567890abcdef1234567890abcdef12"
 * ```
 */
export function normalizeEthVaultAddress(vaultAddress) {
    const address = vaultAddress.toLowerCase().replace("0x", "");
    // For these specific vault addresses, return the known checksummed versions
    if (address === ETHEREUM_BD_MAINNET_VAULT.toLowerCase().replace("0x", "")) {
        return ETHEREUM_BD_MAINNET_VAULT;
    }
    if (address === ETHEREUM_HOODI_BD_PUBLIC_VAULT.toLowerCase().replace("0x", "")) {
        return ETHEREUM_HOODI_BD_PUBLIC_VAULT;
    }
    // For other addresses, return lowercased with 0x prefix
    return `0x${address}`;
}
/**
 * Compares two vault addresses in a case-insensitive manner.
 *
 * @param address1 - First vault address to compare
 * @param address2 - Second vault address to compare
 * @returns True if the addresses are the same (case-insensitive), false otherwise
 *
 * @example
 * ```typescript
 * import { compareEthVaultAddresses } from "@blockdaemon/bd-sdk";
 *
 * // These return true
 * const same1 = compareEthVaultAddresses(
 *   "0x5799be451ee9fddd171c23bf5be86eccfe1f062f",
 *   "0x5799BE451eE9fddD171c23bf5BE86eCcFE1f062F"
 * );
 *
 * const same2 = compareEthVaultAddresses(
 *   "0X5799BE451EE9FDDD171C23BF5BE86ECCFE1F062F",
 *   "0x5799BE451eE9fddD171c23bf5BE86eCcFE1f062F"
 * );
 * ```
 */
export function compareEthVaultAddresses(address1, address2) {
    return (normalizeEthVaultAddress(address1).toLowerCase() ===
        normalizeEthVaultAddress(address2).toLowerCase());
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidmF1bHQtYWRkcmVzc2VzLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21haW4vc2hhcmVkL3ZhdWx0LWFkZHJlc3Nlcy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTs7Ozs7Ozs7OztHQVVHO0FBR0gsT0FBTyxFQUFFLGVBQWUsRUFBRSxnQkFBZ0IsRUFBRSxNQUFNLFNBQVMsQ0FBQztBQUM1RCxPQUFPLEVBQUUsd0JBQXdCLEVBQUUsMEJBQTBCLEVBQUUsTUFBTSxTQUFTLENBQUM7QUFFL0U7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FzQkc7QUFFSDs7O0dBR0c7QUFDSCxNQUFNLENBQUMsTUFBTSxjQUFjLEdBQUcsUUFBUSxDQUFDO0FBRXZDOzs7O0dBSUc7QUFDSCxNQUFNLENBQUMsTUFBTSxtQkFBbUIsR0FBRyxVQUFVLGNBQWMsRUFBRSxDQUFDO0FBQzlELE1BQU0sQ0FBQyxNQUFNLHNCQUFzQixHQUFHLFVBQVUsQ0FBQztBQUVqRDs7O0dBR0c7QUFDSCxNQUFNLENBQUMsTUFBTSxzQkFBc0IsR0FBRyxrQ0FBa0MsQ0FBQztBQUN6RSxNQUFNLENBQUMsTUFBTSxzQkFBc0IsR0FBRyxrQ0FBa0MsQ0FBQztBQUV6RTs7R0FFRztBQUNILE1BQU0sQ0FBQyxNQUFNLG9CQUFvQixHQUFHLFVBQVUsc0JBQXNCLEVBQUUsQ0FBQztBQUN2RSxNQUFNLENBQUMsTUFBTSxvQkFBb0IsR0FBRyxVQUFVLHNCQUFzQixFQUFFLENBQUM7QUFFdkUsTUFBTSw0QkFBNEIsR0FBMkI7SUFDM0QsT0FBTyxFQUFFLHNCQUFzQjtJQUMvQixPQUFPLEVBQUUsc0JBQXNCO0NBQ2hDLENBQUM7QUFFRjs7O0dBR0c7QUFDSCxTQUFTLDJCQUEyQixDQUFDLE9BQWU7SUFDbEQsSUFBSSxDQUFDLE9BQU8sQ0FBQyxVQUFVLENBQUMsU0FBUyxDQUFDO1FBQUUsT0FBTyxPQUFPLENBQUM7SUFDbkQsTUFBTSxTQUFTLEdBQUcsT0FBTyxDQUFDLEtBQUssQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQztJQUN4QyxNQUFNLFdBQVcsR0FBRyxTQUFTLENBQUMsQ0FBQyxDQUFDLDRCQUE0QixDQUFDLFNBQVMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxTQUFTLENBQUM7SUFDcEYsT0FBTyxXQUFXLENBQUMsQ0FBQyxDQUFDLFVBQVUsV0FBVyxFQUFFLENBQUMsQ0FBQyxDQUFDLE9BQU8sQ0FBQztBQUN6RCxDQUFDO0FBQ0Q7Ozs7R0FJRztBQUNILE1BQU0sQ0FBQyxNQUFNLDhCQUE4QixHQUFHLDRDQUE0QyxDQUFDO0FBRTNGOzs7O0dBSUc7QUFDSCxNQUFNLENBQUMsTUFBTSx5QkFBeUIsR0FBRyw0Q0FBNEMsQ0FBQztBQUV0Rjs7Ozs7Ozs7OztHQVVHO0FBQ0gsTUFBTSxDQUFDLE1BQU0sdUJBQXVCLEdBQUcsOENBQThDLENBQUM7QUFFdEY7Ozs7Ozs7Ozs7OztHQVlHO0FBQ0gsTUFBTSxDQUFDLE1BQU0sMkJBQTJCLEdBQUcsOENBQThDLENBQUM7QUFFMUY7Ozs7Ozs7Ozs7Ozs7Ozs7R0FnQkc7QUFDSCxNQUFNLENBQUMsTUFBTSxtQkFBbUIsR0FBMkI7SUFDekQsQ0FBQyxtQkFBbUIsQ0FBQyxFQUFFLDhCQUE4QixFQUFFLDJDQUEyQztJQUNsRyxDQUFDLHNCQUFzQixDQUFDLEVBQUUseUJBQXlCLEVBQUUsOENBQThDO0lBQ25HLENBQUMsb0JBQW9CLENBQUMsRUFBRSx1QkFBdUIsRUFBRSx5Q0FBeUM7SUFDMUYsQ0FBQyxvQkFBb0IsQ0FBQyxFQUFFLDJCQUEyQixFQUFFLDBEQUEwRDtDQUNoSCxDQUFDO0FBRUY7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBb0JHO0FBQ0gsTUFBTSxVQUFVLGVBQWUsQ0FBQyxPQUFxQjtJQUNuRCxJQUFJLFNBQWlCLENBQUM7SUFDdEIsSUFBSSxDQUFDO1FBQ0gsU0FBUyxHQUFHLGVBQWUsQ0FBQyxPQUFPLENBQUMsQ0FBQztJQUN2QyxDQUFDO0lBQUMsTUFBTSxDQUFDO1FBQ1AsTUFBTSxJQUFJLDBCQUEwQixDQUFDLE9BQU8sQ0FBQyxDQUFDO0lBQ2hELENBQUM7SUFFRCxJQUFJLGlCQUF5QixDQUFDO0lBQzlCLElBQUksQ0FBQztRQUNILGlCQUFpQixHQUFHLGdCQUFnQixDQUFDLE9BQU8sRUFBRSxTQUFTLENBQUMsQ0FBQztJQUMzRCxDQUFDO0lBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztRQUNYLE1BQU0sTUFBTSxHQUFHLENBQUMsWUFBWSxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxPQUFPLENBQUMsQ0FBQyxDQUFDLFNBQVMsQ0FBQztRQUMxRCxNQUFNLElBQUksd0JBQXdCLENBQUMsT0FBTyxFQUFFLFNBQVMsRUFBRSxNQUFNLENBQUMsQ0FBQztJQUNqRSxDQUFDO0lBRUQsTUFBTSxZQUFZLEdBQ2hCLFNBQVMsS0FBSyxRQUFRLENBQUMsQ0FBQyxDQUFDLDJCQUEyQixDQUFDLGlCQUFpQixDQUFDLENBQUMsQ0FBQyxDQUFDLGlCQUFpQixDQUFDO0lBQzlGLE9BQU8sbUJBQW1CLENBQUMsWUFBWSxDQUFDLENBQUM7QUFDM0MsQ0FBQztBQUVEOzs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQW9CRztBQUNILE1BQU0sVUFBVSxnQkFBZ0IsQ0FBQyxPQUFxQjtJQUNwRCxJQUFJLFNBQWlCLENBQUM7SUFDdEIsSUFBSSxDQUFDO1FBQ0gsU0FBUyxHQUFHLGVBQWUsQ0FBQyxPQUFPLENBQUMsQ0FBQztJQUN2QyxDQUFDO0lBQUMsTUFBTSxDQUFDO1FBQ1AsTUFBTSxJQUFJLDBCQUEwQixDQUFDLE9BQU8sQ0FBQyxDQUFDO0lBQ2hELENBQUM7SUFFRCxJQUFJLGlCQUF5QixDQUFDO0lBQzlCLElBQUksQ0FBQztRQUNILGlCQUFpQixHQUFHLGdCQUFnQixDQUFDLE9BQU8sRUFBRSxTQUFTLENBQUMsQ0FBQztJQUMzRCxDQUFDO0lBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztRQUNYLE1BQU0sTUFBTSxHQUFHLENBQUMsWUFBWSxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQyxPQUFPLENBQUMsQ0FBQyxDQUFDLFNBQVMsQ0FBQztRQUMxRCxNQUFNLElBQUksd0JBQXdCLENBQUMsT0FBTyxFQUFFLFNBQVMsRUFBRSxNQUFNLENBQUMsQ0FBQztJQUNqRSxDQUFDO0lBRUQsTUFBTSxZQUFZLEdBQ2hCLFNBQVMsS0FBSyxRQUFRLENBQUMsQ0FBQyxDQUFDLDJCQUEyQixDQUFDLGlCQUFpQixDQUFDLENBQUMsQ0FBQyxDQUFDLGlCQUFpQixDQUFDO0lBQzlGLE9BQU8sWUFBWSxJQUFJLG1CQUFtQixDQUFDO0FBQzdDLENBQUM7QUFFRDs7Ozs7Ozs7Ozs7OztHQWFHO0FBQ0gsK0VBQStFO0FBRS9FOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FxQkc7QUFDSCxNQUFNLFVBQVUsd0JBQXdCLENBQUMsWUFBb0I7SUFDM0QsTUFBTSxPQUFPLEdBQUcsWUFBWSxDQUFDLFdBQVcsRUFBRSxDQUFDLE9BQU8sQ0FBQyxJQUFJLEVBQUUsRUFBRSxDQUFDLENBQUM7SUFFN0QsNEVBQTRFO0lBQzVFLElBQUksT0FBTyxLQUFLLHlCQUF5QixDQUFDLFdBQVcsRUFBRSxDQUFDLE9BQU8sQ0FBQyxJQUFJLEVBQUUsRUFBRSxDQUFDLEVBQUUsQ0FBQztRQUMxRSxPQUFPLHlCQUF5QixDQUFDO0lBQ25DLENBQUM7SUFDRCxJQUFJLE9BQU8sS0FBSyw4QkFBOEIsQ0FBQyxXQUFXLEVBQUUsQ0FBQyxPQUFPLENBQUMsSUFBSSxFQUFFLEVBQUUsQ0FBQyxFQUFFLENBQUM7UUFDL0UsT0FBTyw4QkFBOEIsQ0FBQztJQUN4QyxDQUFDO0lBRUQsd0RBQXdEO0lBQ3hELE9BQU8sS0FBSyxPQUFPLEVBQUUsQ0FBQztBQUN4QixDQUFDO0FBRUQ7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FzQkc7QUFDSCxNQUFNLFVBQVUsd0JBQXdCLENBQUMsUUFBZ0IsRUFBRSxRQUFnQjtJQUN6RSxPQUFPLENBQ0wsd0JBQXdCLENBQUMsUUFBUSxDQUFDLENBQUMsV0FBVyxFQUFFO1FBQ2hELHdCQUF3QixDQUFDLFFBQVEsQ0FBQyxDQUFDLFdBQVcsRUFBRSxDQUNqRCxDQUFDO0FBQ0osQ0FBQyJ9